/**
 * /admin/neopay-settings — super-admin only.
 * Phase B Path B: configure DB-backed NeoPay credentials from the UI so
 * production works without setting env vars in the deployment panel.
 *
 * The form is a partial-update form: leave a field empty to keep its
 * existing value; only fields the admin actually changes are sent.
 * Secrets come back from the server already masked.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Database, KeyRound, Loader2,
  RefreshCw, ShieldCheck, Webhook, XCircle, Zap,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const SOURCE_LABEL = {
  db: { text: "DB", cls: "bg-green-500/15 text-green-600 border-green-500/30" },
  env: { text: "env", cls: "bg-gray-500/15 text-gray-500 border-gray-500/30" },
  none: { text: "missing", cls: "bg-red-500/15 text-red-500 border-red-500/30" },
};

function SourceBadge({ source }) {
  const meta = SOURCE_LABEL[source] || SOURCE_LABEL.none;
  return (
    <span
      className={`inline-flex items-center text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${meta.cls}`}
      data-testid={`source-badge-${source}`}
    >
      {meta.text}
    </span>
  );
}

/**
 * iter141 — Reconciliation line inside the backfill result card.
 * Mirrors the chain integrity verifier into a single "N rows mirrored → N OK on
 * chain" headline so an auditor can grab one screenshot and be done.
 */
function BackfillVerifyReconciliation({ totalWritten, verifyState }) {
  if (verifyState.running) {
    return (
      <p
        className="text-xs text-violet-700 dark:text-violet-300 pt-1 inline-flex items-center gap-1.5"
        data-testid="backfill-verify-pending"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Verifying chain integrity now…
      </p>
    );
  }
  const result = verifyState.result;
  if (!result) {
    return (
      <p className="text-xs text-muted-foreground pt-1">
        Auto-verifier didn’t run — click <span className="font-bold">Verify chain integrity</span> above.
      </p>
    );
  }
  // Headline matches "N rows mirrored → N OK on chain" exactly when totals agree.
  const ok = result.ok || 0;
  const checked = result.checked || 0;
  const allGood = result.healthy && (totalWritten === 0 || ok >= totalWritten);
  return (
    <div
      className={`pt-1 text-[12px] font-bold inline-flex items-center gap-2 ${
        allGood ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'
      }`}
      data-testid="backfill-verify-reconciliation"
    >
      <span aria-hidden>{allGood ? '✓' : '⚠'}</span>
      <span className="tabular-nums">
        {totalWritten} row{totalWritten === 1 ? '' : 's'} mirrored
      </span>
      <span aria-hidden>→</span>
      <span className="tabular-nums">
        {ok}/{checked} OK on chain
      </span>
      {!allGood && (
        <span className="text-muted-foreground font-normal">
          ({result.mismatched_count || 0} mismatched · {result.missing_on_chain_count || 0} missing · {result.not_mirrored_count || 0} unmirrored)
        </span>
      )}
    </div>
  );
}

export default function NeoPaySettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Form state. Empty = no change.
  const [form, setForm] = useState({
    NEOPAY_BASE_URL: "",
    NEOPAY_API_KEY: "",
    NEOPAY_WEBHOOK_SECRET: "",
    NEOPAY_VPA: "",
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_super_admin) {
      toast.error("Super-admin only");
      navigate("/dashboard");
      return;
    }
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await api.get("/neopay/admin/settings");
      setSettings(r.data);
      // Pre-fill the URL & VPA fields (non-secrets) so the admin can edit;
      // leave secret fields empty (placeholder shows the mask).
      setForm({
        NEOPAY_BASE_URL: r.data.NEOPAY_BASE_URL || "",
        NEOPAY_API_KEY: "",
        NEOPAY_WEBHOOK_SECRET: "",
        NEOPAY_VPA: r.data.NEOPAY_VPA || "splitease-audit@neopay",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Build a partial body — only include non-empty fields the user actually
    // changed (URL/VPA may match what we pre-filled; that's fine, we still
    // send them, the server upsert is idempotent).
    const body = {};
    Object.keys(form).forEach((k) => {
      if (form[k] && form[k].trim() !== "") body[k] = form[k].trim();
    });
    if (Object.keys(body).length === 0) {
      toast.error("Nothing to save — all fields are empty");
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const r = await api.put("/neopay/admin/settings", body);
      setSettings(r.data);
      // Wipe the secret fields so the placeholder mask is visible again.
      setForm((f) => ({
        ...f,
        NEOPAY_API_KEY: "",
        NEOPAY_WEBHOOK_SECRET: "",
      }));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/neopay/admin/settings/test");
      setTestResult(r.data);
      if (r.data.ok) toast.success(`Connected as ${r.data.vpa || r.data.business_id || "NeoPay business"}`);
      else toast.error(r.data.error || "Test failed");
    } catch (e) {
      const err = e.response?.data?.detail || e.message;
      setTestResult({ ok: false, error: err });
      toast.error(err);
    } finally {
      setTesting(false);
    }
  };

  // iter130 — flip a mirror toggle; saves instantly (no Save button required)
  const handleToggleMirror = async (key, next) => {
    // Optimistic
    setSettings((s) => ({ ...s, [key]: next }));
    try {
      const r = await api.put("/neopay/admin/settings", { [key]: next });
      setSettings(r.data);
      toast.success(`${key.replace('MIRROR_', '').toLowerCase()} mirror ${next ? 'enabled' : 'disabled'}`);
    } catch (e) {
      // Rollback
      setSettings((s) => ({ ...s, [key]: !next }));
      toast.error(e.response?.data?.detail || "Failed to update toggle");
    }
  };

  // iter136 — Verify chain integrity
  const [verifyState, setVerifyState] = useState({ running: false, result: null, autoTriggered: false });
  // iter137 — Auto-repair busy state per tx_id
  const [repairingMap, setRepairingMap] = useState({});

  const handleRepairRow = async (mismatch) => {
    setRepairingMap((m) => ({ ...m, [mismatch.tx_id]: true }));
    try {
      const r = await api.post("/admin/neopay/repair-row", {
        kind: mismatch.kind,
        id: mismatch.id,
      });
      toast.success(`Repaired. New tx: ${(r.data.new_tx_id || '').slice(0, 12)}…`);
      // Remove the entry from the local mismatch list
      setVerifyState((s) => ({
        ...s,
        result: s.result ? {
          ...s.result,
          ok: (s.result.ok || 0) + 1,
          mismatched_count: Math.max(0, (s.result.mismatched_count || 1) - 1),
          mismatched: (s.result.mismatched || []).filter((x) => x.tx_id !== mismatch.tx_id),
          healthy:
            (s.result.mismatched || []).filter((x) => x.tx_id !== mismatch.tx_id).length === 0
            && (s.result.missing_on_chain_count || 0) === 0
            && (s.result.errors_count || 0) === 0,
        } : s.result,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repair failed");
    } finally {
      setRepairingMap((m) => ({ ...m, [mismatch.tx_id]: false }));
    }
  };
  const handleVerifyChain = async ({ autoTriggered = false } = {}) => {
    setVerifyState({ running: true, result: null, autoTriggered });
    try {
      const r = await api.post("/admin/neopay/verify-chain", {});
      const data = r.data;
      // iter142 — verify is now a background job. POST returns
      // `{job_id, status:"running", result: {...initial-empty...}}`.
      if (!data.job_id) {
        // Backward-compat: old endpoint returned the result inline.
        setVerifyState({ running: false, result: data, autoTriggered });
        if (data.healthy) {
          toast.success(`✓ Chain healthy — ${data.ok} rows match (${data.checked} checked).`);
        } else {
          toast.warning(
            `Chain has issues: ${data.mismatched_count || 0} mismatched, ${data.missing_on_chain_count || 0} missing, ${data.not_mirrored_count || 0} unmirrored.`,
            { duration: 9000 }
          );
        }
        return;
      }
      setVerifyState({ running: true, result: data.result, autoTriggered });
      pollVerifyJob(data.job_id, autoTriggered);
    } catch (e) {
      setVerifyState({ running: false, result: null, autoTriggered });
      toast.error(e.response?.data?.detail || "Verify failed");
    }
  };

  const pollVerifyJob = (jobId, autoTriggered) => {
    const tick = async () => {
      try {
        const r = await api.get(`/admin/neopay/verify-chain/${jobId}`);
        const status = r.data.status;
        setVerifyState({
          running: status === "running",
          result: r.data.result,
          autoTriggered,
        });
        if (status === "done") {
          const v = r.data.result;
          if (v.healthy) {
            toast.success(`✓ Chain healthy — ${v.ok} rows match (${v.checked} checked).`);
          } else {
            toast.warning(
              `Chain has issues: ${v.mismatched_count || 0} mismatched, ${v.missing_on_chain_count || 0} missing, ${v.not_mirrored_count || 0} unmirrored.`,
              { duration: 9000 }
            );
          }
          return;
        }
        if (status === "error") {
          toast.error(r.data.error || "Verify errored");
          setVerifyState((s) => ({ ...s, running: false }));
          return;
        }
        setTimeout(tick, 2500);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to fetch verify status");
        setVerifyState((s) => ({ ...s, running: false }));
      }
    };
    tick();
  };

  // iter140 — One-time NeoPay backfill UI
  const [backfillState, setBackfillState] = useState({
    running: false,
    result: null,
    dryRun: true,
    jobId: null,
  });

  const handleBackfill = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm(
        "Run NeoPay backfill for ALL groups (write mode)?\n\n" +
        "This will mirror every historical group, member-join, expense, and fund " +
        "transaction that isn't already on-chain. Large tenants may take many " +
        "minutes — the job runs in the background and this page shows live " +
        "progress. It's idempotent — already-mirrored rows are skipped. Continue?"
      );
      if (!ok) return;
    }
    setBackfillState((s) => ({ ...s, running: true, dryRun, result: null, jobId: null }));
    try {
      const r = await api.post("/admin/neopay/backfill", {
        dry_run: dryRun,
        sleep_ms: 30,
        exclude_test_groups: !!backfillState.excludeTestGroups,
      });
      const data = r.data;

      // Dry runs return the full summary synchronously.
      if (dryRun) {
        setBackfillState({ running: false, dryRun, result: data, jobId: null });
        const s = data;
        const totalNew =
          (s.groups_genesis?.created || 0) +
          (s.members_joined?.created || 0) +
          (s.expenses?.created || 0) +
          (s.fund_txns?.created || 0);
        toast.success(`Dry run complete — ${totalNew} row(s) would be mirrored across ${s.groups_scanned} group(s).`);
        return;
      }

      // Real run → got back a job_id. Poll until "done".
      const jobId = data.job_id;
      setBackfillState({ running: true, dryRun: false, result: data.summary, jobId });
      toast.info("Backfill started — running in background. Progress shown live.");
      pollJob(jobId);
    } catch (e) {
      setBackfillState((s) => ({ ...s, running: false, result: null, jobId: null }));
      toast.error(e.response?.data?.detail || "Backfill failed");
    }
  };

  // iter140 — Poll a backfill job_id until it's done or errored.
  const pollJob = async (jobId) => {
    const tick = async () => {
      try {
        const r = await api.get(`/admin/neopay/backfill/${jobId}`);
        const status = r.data.status;
        setBackfillState((s) => ({
          ...s,
          jobId,
          result: r.data.summary,
          running: status === "running",
        }));
        if (status === "done") {
          const s = r.data.summary;
          toast.success(`Backfill complete — ${s.total_writes || 0} row(s) mirrored across ${s.groups_scanned || 0} group(s).`);
          // Auto-trigger the chain integrity verifier
          handleVerifyChain({ autoTriggered: true });
          return;
        }
        if (status === "error") {
          toast.error(r.data.error || "Backfill errored");
          return;
        }
        // Still running — schedule next tick.
        setTimeout(tick, 2500);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to fetch backfill status");
        setBackfillState((s) => ({ ...s, running: false }));
      }
    };
    tick();
  };

  if (authLoading || loading) {
    return (
      <div className="p-6 flex items-center justify-center text-muted-foreground" data-testid="neopay-settings-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading…
      </div>
    );
  }

  const sources = settings?.sources || {};

  return (
    <div className="max-w-3xl lg:max-w-none mx-auto p-4 md:p-6 space-y-4" data-testid="neopay-settings-page">
      <button
        onClick={() => navigate("/admin")}
        className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
        data-testid="back-to-admin"
      >
        <ArrowLeft className="w-3 h-3" /> back to admin
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Database className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-display font-bold">NeoPay credentials</h1>
          <p className="text-xs text-muted-foreground">
            Configure the four NeoPay creds from here — encrypted in Mongo, no .env edit needed.
          </p>
        </div>
      </div>

      {/* Status header */}
      <Card className="p-4 border-2 rounded-xl" data-testid="neopay-settings-status">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm flex items-center gap-2">
            {settings?.configured ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="font-medium">Configured: <span className="text-green-600">YES</span></span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-amber-500" />
                <span className="font-medium">Configured: <span className="text-amber-500">NO</span></span>
              </>
            )}
            <span className="text-xs text-muted-foreground">— DB-stored values shadow env</span>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchSettings}
              data-testid="reload-settings-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> reload
            </Button>
            <Button
              size="sm"
              onClick={handleTest}
              disabled={testing || !settings?.NEOPAY_BASE_URL}
              data-testid="test-connection-btn"
            >
              {testing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> testing…</> : <><Zap className="w-3 h-3 mr-1" /> Test connection</>}
            </Button>
          </div>
        </div>

        {testResult && (
          <div
            className={`mt-3 text-xs px-3 py-2 rounded-md border ${
              testResult.ok
                ? "border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400"
                : "border-red-500/30 bg-red-500/10 text-red-600"
            }`}
            data-testid="test-result-banner"
          >
            {testResult.ok ? (
              <>Connected · business <span className="font-mono">{testResult.business_id || "—"}</span> · vpa <span className="font-mono">{testResult.vpa || "—"}</span></>
            ) : (
              <>Connection failed: {testResult.error}</>
            )}
          </div>
        )}
      </Card>

      {/* Form */}
      <Card className="p-4 border-2 rounded-xl space-y-4" data-testid="neopay-settings-form">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="np-base-url" className="text-xs font-semibold inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> NEOPAY_BASE_URL
            </Label>
            <SourceBadge source={sources.NEOPAY_BASE_URL || "none"} />
          </div>
          <Input
            id="np-base-url"
            placeholder="https://your-neopay-deployment.example.com"
            value={form.NEOPAY_BASE_URL}
            onChange={(e) => setForm({ ...form, NEOPAY_BASE_URL: e.target.value })}
            data-testid="input-base-url"
          />
          <p className="text-xs text-muted-foreground">
            The base URL of your NeoPay instance (no trailing slash).
          </p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="np-api-key" className="text-xs font-semibold inline-flex items-center gap-1">
              <KeyRound className="w-3 h-3" /> NEOPAY_API_KEY
            </Label>
            <SourceBadge source={sources.NEOPAY_API_KEY || "none"} />
          </div>
          <Input
            id="np-api-key"
            type="password"
            placeholder={settings?.NEOPAY_API_KEY_MASKED || "neopay_bk_…"}
            value={form.NEOPAY_API_KEY}
            onChange={(e) => setForm({ ...form, NEOPAY_API_KEY: e.target.value })}
            data-testid="input-api-key"
            autoComplete="new-password"
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to keep existing. Issued from NeoPay → Business → API Keys.
          </p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="np-webhook" className="text-xs font-semibold inline-flex items-center gap-1">
              <Webhook className="w-3 h-3" /> NEOPAY_WEBHOOK_SECRET
            </Label>
            <SourceBadge source={sources.NEOPAY_WEBHOOK_SECRET || "none"} />
          </div>
          <Input
            id="np-webhook"
            type="password"
            placeholder={settings?.NEOPAY_WEBHOOK_SECRET_MASKED || "whsec_…"}
            value={form.NEOPAY_WEBHOOK_SECRET}
            onChange={(e) => setForm({ ...form, NEOPAY_WEBHOOK_SECRET: e.target.value })}
            data-testid="input-webhook-secret"
            autoComplete="new-password"
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to keep existing. Used to verify HMAC on inbound webhooks.
          </p>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="np-vpa" className="text-xs font-semibold">
              NEOPAY_VPA
            </Label>
            <SourceBadge source={sources.NEOPAY_VPA || "none"} />
          </div>
          <Input
            id="np-vpa"
            placeholder="splitease-audit@neopay"
            value={form.NEOPAY_VPA}
            onChange={(e) => setForm({ ...form, NEOPAY_VPA: e.target.value })}
            data-testid="input-vpa"
          />
          <p className="text-xs text-muted-foreground">
            The audit business VPA on NeoPay (we self-transfer to mirror events).
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-settings-btn"
          >
            {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> saving…</> : "Save"}
          </Button>
        </div>

        {/* iter130 — granular mirror toggles. Settlements are always mirrored. */}
        <div className="pt-4 mt-2 border-t space-y-3" data-testid="mirror-toggles-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-foreground/80">
              What to mirror to NeoPay
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Settlement transfers are <span className="font-semibold">always</span> mirrored (audit trail).
              Toggle the rest individually. Changes apply to new events instantly.
            </p>
          </div>

          {[
            { key: "MIRROR_GROUPS", title: "Group creation", desc: "Writes a genesis ledger row for each new group (memo: se|group_created|…)." },
            { key: "MIRROR_MEMBERS", title: "Member joins", desc: "Mirrors every member added (real or ghost) as a member_joined ledger entry." },
            { key: "MIRROR_EXPENSES", title: "Approved expenses", desc: "Mirrors each expense the moment it's approved (memo includes payer + amount)." },
          ].map(({ key, title, desc }) => {
            const on = !!settings?.[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleToggleMirror(key, !on)}
                data-testid={`toggle-${key.toLowerCase()}`}
                className="w-full flex items-start gap-3 p-3 rounded-xl border hover:bg-accent/40 transition text-left"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{title}</p>
                  <p className="text-xs text-muted-foreground leading-snug">{desc}</p>
                </div>
                <span
                  className={`shrink-0 inline-flex items-center w-10 h-5 rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-muted'}`}
                  aria-hidden
                >
                  <span
                    className={`w-4 h-4 rounded-full bg-white shadow transform transition-transform ${on ? 'translate-x-5' : 'translate-x-1'}`}
                  />
                </span>
              </button>
            );
          })}

          <div className="flex items-start gap-3 p-3 rounded-xl border-2 border-dashed opacity-70">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold inline-flex items-center gap-1.5">
                Coin transfers / settlements
                <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-bold">
                  Always on
                </span>
              </p>
              <p className="text-xs text-muted-foreground leading-snug">
                Cannot be disabled — this is the core SplitEasy ↔ NeoPay audit trail.
              </p>
            </div>
          </div>
        </div>

        {/* iter136 — Chain Integrity Verifier (super-admin only) */}
        <div className="pt-4 mt-2 border-t space-y-3" data-testid="verify-chain-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-foreground/80">
              Chain integrity verifier
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Walks every group's ledger, fetches each on-chain memo, and confirms it matches the app. Use this before/after a redeploy or at audit time.
            </p>
          </div>
          <Button
            onClick={handleVerifyChain}
            disabled={verifyState.running}
            data-testid="verify-chain-btn"
            className="w-full rounded-xl h-10 font-bold"
            variant="outline"
          >
            {verifyState.running ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Verifying chain…</>
            ) : (
              "Verify chain integrity"
            )}
          </Button>

          {verifyState.result && (
            <div
              className={`p-3 rounded-xl border-2 space-y-2 ${
                verifyState.running
                  ? 'border-violet-500/40 bg-violet-500/5'
                  : verifyState.result.healthy
                    ? 'border-emerald-500/40 bg-emerald-500/5'
                    : 'border-amber-500/40 bg-amber-500/5'
              }`}
              data-testid="verify-chain-result"
            >
              <p className="text-sm font-bold flex items-center justify-between gap-2">
                <span>
                  {verifyState.running
                    ? `⏳ Verifying — ${verifyState.result.groups_scanned || 0}/${verifyState.result.groups_total || '?'} groups`
                    : verifyState.result.healthy
                      ? `✓ Healthy — ${verifyState.result.ok || 0}/${verifyState.result.checked || 0} on-chain rows match`
                      : '⚠ Integrity issues found'}
                </span>
                {verifyState.autoTriggered && (
                  <span
                    className="text-xs uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/30"
                    title="Triggered automatically after a successful backfill"
                    data-testid="verify-auto-triggered-badge"
                  >
                    Auto-verified
                  </span>
                )}
              </p>

              {/* Live progress bar while running */}
              {verifyState.running && (verifyState.result.groups_total || 0) > 0 && (
                <div className="space-y-1">
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-violet-500 transition-all"
                      style={{
                        width: `${Math.min(100, ((verifyState.result.groups_scanned || 0) / (verifyState.result.groups_total || 1)) * 100)}%`,
                      }}
                      data-testid="verify-progress-bar"
                    />
                  </div>
                  {verifyState.result.current_group?.name && (
                    <p className="text-xs text-muted-foreground truncate" data-testid="verify-current-group">
                      {verifyState.result.current_group.id === '_fetch'
                        ? `${verifyState.result.current_group.name} · ${verifyState.result.neopay_txns_seen || 0} txns indexed so far`
                        : <>Checking <span className="font-semibold">{verifyState.result.current_group.name}</span> · {verifyState.result.checked || 0} on-chain rows verified so far</>}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">OK rows:</span> <span className="font-bold tabular-nums">{verifyState.result.ok || 0}</span></div>
                <div><span className="text-muted-foreground">Checked:</span> <span className="font-bold tabular-nums">{verifyState.result.checked || 0}</span></div>
                <div><span className="text-muted-foreground">Mismatched:</span> <span className={`font-bold tabular-nums ${verifyState.result.mismatched_count ? 'text-red-600' : ''}`}>{verifyState.result.mismatched_count || 0}</span></div>
                <div><span className="text-muted-foreground">Missing on-chain:</span> <span className={`font-bold tabular-nums ${verifyState.result.missing_on_chain_count ? 'text-red-600' : ''}`}>{verifyState.result.missing_on_chain_count || 0}</span></div>
                <div><span className="text-muted-foreground">Not mirrored:</span> <span className={`font-bold tabular-nums ${verifyState.result.not_mirrored_count ? 'text-amber-600' : ''}`}>{verifyState.result.not_mirrored_count || 0}</span></div>
                <div><span className="text-muted-foreground">Groups scanned:</span> <span className="font-bold tabular-nums">{verifyState.result.groups_scanned || 0}</span></div>
              </div>
              {verifyState.result.mismatched_count > 0 && (
                <details className="text-xs" data-testid="verify-mismatched-details">
                  <summary className="cursor-pointer text-red-700 dark:text-red-300 font-semibold">
                    Show {Math.min(50, verifyState.result.mismatched_count)} mismatch{verifyState.result.mismatched_count > 1 ? 'es' : ''}
                  </summary>
                  <ul className="mt-1 space-y-1.5 max-h-60 overflow-auto">
                    {(verifyState.result.mismatched || []).map((m) => {
                      const repairing = !!repairingMap[m.tx_id];
                      return (
                        <li key={m.tx_id} className="p-2 rounded bg-background border space-y-1.5">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-xs font-bold">{m.kind} · {m.label}</p>
                              <p className="font-mono text-xs text-red-600 dark:text-red-400 truncate">expected: {m.expected}</p>
                              <p className="font-mono text-xs text-muted-foreground truncate">actual: {m.actual}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRepairRow(m)}
                              disabled={repairing}
                              data-testid={`repair-row-${m.tx_id}`}
                              className="shrink-0 px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 disabled:opacity-50"
                            >
                              {repairing ? '…' : 'Auto-repair'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </details>
              )}
              {verifyState.result.not_mirrored_count > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  💡 Tip: scroll down to <span className="font-bold">NeoPay Backfill</span> to mirror the {verifyState.result.not_mirrored_count} unmirrored row{verifyState.result.not_mirrored_count > 1 ? 's' : ''}.
                </p>
              )}
            </div>
          )}
        </div>

        {/* iter140 — One-time NeoPay backfill (super-admin only) */}
        <div className="pt-4 mt-2 border-t space-y-3" data-testid="backfill-section">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-foreground/80">
              NeoPay Backfill
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Mirror every historical group, member-join, expense, and fund
              transaction that isn't already on-chain. <span className="font-semibold">Scans the entire tenant</span> (every group in the DB, including ones you aren't a member of) — that's the super-admin scope. Idempotent — rows with an existing <code className="font-mono">neopay_tx_id</code> are skipped. Always start with a dry run.
            </p>
          </div>
          <label
            className="flex items-center gap-2 text-xs cursor-pointer select-none"
            data-testid="backfill-exclude-test-label"
          >
            <input
              type="checkbox"
              checked={backfillState.excludeTestGroups || false}
              disabled={backfillState.running}
              onChange={(e) => setBackfillState((s) => ({ ...s, excludeTestGroups: e.target.checked }))}
              data-testid="backfill-exclude-test-checkbox"
            />
            <span>
              Exclude obvious test groups (names matching <code className="font-mono text-xs">TEST_*</code>, <code className="font-mono text-xs">NXCReq_*</code>, <code className="font-mono text-xs">NetPos_*</code>, <code className="font-mono text-xs">iter*</code>, etc.)
            </span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => handleBackfill(true)}
              disabled={backfillState.running}
              data-testid="backfill-dry-run-btn"
              className="rounded-xl h-10 font-bold"
              variant="outline"
            >
              {backfillState.running && backfillState.dryRun ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Dry run…</>
              ) : (
                "Dry run"
              )}
            </Button>
            <Button
              onClick={() => handleBackfill(false)}
              disabled={backfillState.running}
              data-testid="backfill-run-btn"
              className="rounded-xl h-10 font-bold bg-amber-500 hover:bg-amber-600 text-white"
            >
              {backfillState.running && !backfillState.dryRun ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running…</>
              ) : (
                "Run backfill"
              )}
            </Button>
          </div>

          {backfillState.result && (
            <div
              className="p-3 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 space-y-2"
              data-testid="backfill-result"
            >
              <p className="text-sm font-bold flex items-center justify-between gap-2">
                <span>
                  {backfillState.result.dry_run
                    ? '🔍 Dry run'
                    : backfillState.running ? '⏳ Backfilling…' : '✓ Backfill'}
                  {' — '}
                  {backfillState.running
                    ? `${backfillState.result.groups_scanned || 0}/${backfillState.result.groups_total || '?'} groups`
                    : `${backfillState.result.groups_scanned} group${backfillState.result.groups_scanned === 1 ? '' : 's'} scanned`}
                </span>
                {backfillState.running && (
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                )}
              </p>

              {/* Live progress bar (real-run only) */}
              {!backfillState.result.dry_run && backfillState.result.groups_total > 0 && (
                <div className="space-y-1">
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{
                        width: `${Math.min(100, ((backfillState.result.groups_scanned || 0) / (backfillState.result.groups_total || 1)) * 100)}%`,
                      }}
                      data-testid="backfill-progress-bar"
                    />
                  </div>
                  {backfillState.running && backfillState.result.current_group?.name && (
                    <p className="text-xs text-muted-foreground truncate" data-testid="backfill-current-group">
                      Working on <span className="font-semibold">{backfillState.result.current_group.name}</span> · {backfillState.result.total_writes || 0} row{(backfillState.result.total_writes || 0) === 1 ? '' : 's'} mirrored so far
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["Group genesis", backfillState.result.groups_genesis, "backfill-stat-groups"],
                  ["Member joins", backfillState.result.members_joined, "backfill-stat-members"],
                  ["Expenses",     backfillState.result.expenses,       "backfill-stat-expenses"],
                  ["Fund txns",    backfillState.result.fund_txns,      "backfill-stat-fund"],
                ].map(([label, stat, tid]) => (
                  <div key={label} className="p-2 rounded-lg bg-background border" data-testid={tid}>
                    <p className="text-muted-foreground text-xs uppercase tracking-wider font-bold">{label}</p>
                    <p className="font-bold tabular-nums">
                      <span className="text-emerald-600 dark:text-emerald-400">+{stat?.created || 0}</span>
                      <span className="text-muted-foreground mx-1">·</span>
                      <span title="skipped (already mirrored)">{stat?.skipped || 0} skip</span>
                      {(stat?.errors || 0) > 0 && (
                        <>
                          <span className="text-muted-foreground mx-1">·</span>
                          <span className="text-red-600">{stat.errors} err</span>
                        </>
                      )}
                    </p>
                  </div>
                ))}
              </div>
              {(backfillState.result.sample || []).length > 0 && (
                <details className="text-xs" data-testid="backfill-sample-details">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-semibold">
                    Show sample writes ({backfillState.result.sample.length})
                  </summary>
                  <ul className="mt-1 space-y-1 max-h-48 overflow-auto font-mono text-xs">
                    {backfillState.result.sample.map((w, i) => (
                      <li key={i} className="px-2 py-1 rounded bg-background border truncate">
                        <span className="font-bold">{w.kind}</span> · {w.label || w.name || w.id}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* iter143 — Groups in scope (so admin can spot test pollution) */}
              {(backfillState.result.groups_in_scope || []).length > 0 && (
                <details className="text-xs" data-testid="backfill-scope-details">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-semibold">
                    Groups in scope ({backfillState.result.groups_in_scope.length})
                  </summary>
                  <ul className="mt-1 space-y-0.5 max-h-48 overflow-auto text-xs">
                    {backfillState.result.groups_in_scope.map((g) => (
                      <li key={g.id} className="px-2 py-0.5 rounded bg-background border truncate" title={g.id}>
                        {g.name || <span className="text-muted-foreground italic">(no name)</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {(backfillState.result.groups_skipped_as_test || 0) > 0 && (
                <details className="text-xs" data-testid="backfill-excluded-details">
                  <summary className="cursor-pointer text-violet-700 dark:text-violet-300 font-semibold">
                    Excluded as test ({backfillState.result.groups_skipped_as_test})
                  </summary>
                  <ul className="mt-1 space-y-0.5 max-h-32 overflow-auto text-xs">
                    {(backfillState.result.groups_excluded || []).map((g) => (
                      <li key={g.id} className="px-2 py-0.5 rounded bg-background border truncate" title={g.id}>
                        <span className="opacity-70 line-through">{g.name}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {backfillState.result.dry_run && (
                <p className="text-xs text-amber-700 dark:text-amber-300 pt-1">
                  Looks good? Click <span className="font-bold">Run backfill</span> to write to NeoPay.
                </p>
              )}
              {!backfillState.result.dry_run && !backfillState.running && (
                <BackfillVerifyReconciliation
                  totalWritten={backfillState.result.total_writes || 0}
                  verifyState={verifyState}
                />
              )}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground border-t pt-2">
          Secrets are encrypted at rest with Fernet (key derived from JWT_SECRET).
          Source badges: <span className="font-medium text-green-600">DB</span> (admin-set) shadows{" "}
          <span className="font-medium text-gray-500">env</span> (.env file).
          Empty fields show <span className="font-medium text-red-500">missing</span> until configured.
        </p>
      </Card>
    </div>
  );
}
